import { api } from './client'

/**
 * F-083 (TASK-0337): compensi fissi per attivita' e relativo maturato.
 * Rotte: `/api/commissions/activity-payout-rules` (lettura: tutti; scrittura: solo Admin) e
 * `/api/commissions/activity-payouts` (esecuzione: solo Admin; elenco: Admin tutti, gli altri solo il proprio).
 * `amount` e i totali sono ASSENTI (non null/0) per chi non ha `CanSeeRealAmounts`.
 */
export type ActivityPayoutEvent = 'AppuntamentoEffettuato' | 'ContrattoGenerato' | 'ContrattoAttivato'
export type ActivityPayoutBeneficiary =
  | 'LeadGenerator'
  | 'AppointmentSetter'
  | 'SalesCloser'
  | 'ContractOwner'
  | 'TeamLeaderDelCloser'
export type ActivityPayoutRuleStatus = 'Bozza' | 'Attiva' | 'Archiviata'

export const ACTIVITY_EVENT_LABELS: Record<ActivityPayoutEvent, string> = {
  AppuntamentoEffettuato: 'Appuntamento effettuato',
  ContrattoGenerato: 'Contratto generato (inviato al backoffice)',
  ContrattoAttivato: 'Contratto attivato',
}

export const ACTIVITY_BENEFICIARY_LABELS: Record<ActivityPayoutBeneficiary, string> = {
  LeadGenerator: 'Lead generator',
  AppointmentSetter: 'Setter (chi fissa l\'appuntamento)',
  SalesCloser: 'Closer',
  ContractOwner: 'Titolare della pratica',
  TeamLeaderDelCloser: 'Team leader del closer',
}

export interface ActivityPayoutRule {
  id: number
  code: string
  name: string
  event: ActivityPayoutEvent
  beneficiary: ActivityPayoutBeneficiary
  amount?: number
  departmentId: number | null
  productId: number | null
  validFrom: string
  validTo: string | null
  priority: number
  version: number
  status: ActivityPayoutRuleStatus
  createdAt: string
  updatedAt: string | null
}

export interface ActivityPayoutRuleBody {
  code?: string
  name: string
  event: ActivityPayoutEvent
  beneficiary: ActivityPayoutBeneficiary
  amount: number
  departmentId: number | null
  productId: number | null
  validFrom: string
  validTo: string | null
  priority: number
}

export interface ActivityPayoutAccrual {
  id: number
  ruleCode: string
  ruleVersion: number
  event: ActivityPayoutEvent
  sourceType: 'Appointment' | 'Contract'
  sourceId: number
  beneficiaryUserId: number
  competenceDate: string
  status: 'Maturata' | 'Stornata'
  stornoReason: string | null
  createdAt: string
  amount?: number
}

export interface ActivityPayoutList {
  count: number
  truncated: boolean
  items: ActivityPayoutAccrual[]
  totalMaturato?: number
  totalStornato?: number
}

export interface ActivityPayoutDiscard {
  event: ActivityPayoutEvent
  sourceType: string
  sourceId: number
  beneficiary: ActivityPayoutBeneficiary
  ruleCode: string
  reason: string
}

export interface ActivityPayoutRunResult {
  dryRun: boolean
  from: string
  to: string
  activeRules: number
  events: { appuntamentoEffettuato: number; contrattoGenerato: number; contrattoAttivato: number }
  newAccruals: number
  newAccrualsAlreadyStornate: number
  alreadyPresent: number
  stornoApplied: number
  newAmount: number
  discardedCount: number
  discarded: ActivityPayoutDiscard[]
}

const RULES = '/api/commissions/activity-payout-rules'
const PAYOUTS = '/api/commissions/activity-payouts'

export const activityPayoutsApi = {
  listRules: (params?: { status?: ActivityPayoutRuleStatus; code?: string }) => {
    const qs = new URLSearchParams()
    if (params?.status) qs.set('status', params.status)
    if (params?.code) qs.set('code', params.code)
    const q = qs.toString()
    return api<ActivityPayoutRule[]>(`${RULES}${q ? `?${q}` : ''}`)
  },
  createRule: (body: ActivityPayoutRuleBody) =>
    api<ActivityPayoutRule>(RULES, { method: 'POST', body: JSON.stringify(body) }),
  updateRule: (id: number, body: ActivityPayoutRuleBody) =>
    api<ActivityPayoutRule>(`${RULES}/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  activateRule: (id: number) => api<ActivityPayoutRule>(`${RULES}/${id}/activate`, { method: 'POST', body: '{}' }),
  archiveRule: (id: number) => api<ActivityPayoutRule>(`${RULES}/${id}/archive`, { method: 'POST', body: '{}' }),
  newVersion: (id: number) => api<ActivityPayoutRule>(`${RULES}/${id}/new-version`, { method: 'POST', body: '{}' }),
  deleteRule: (id: number) => api<void>(`${RULES}/${id}`, { method: 'DELETE' }),

  /** Anteprima (`dryRun` true) o esecuzione. Periodo in `yyyy-MM-dd`, massimo 366 giorni. */
  run: (from: string, to: string, dryRun: boolean) =>
    api<ActivityPayoutRunResult>(
      `${PAYOUTS}/run?from=${from}&to=${to}&dryRun=${dryRun ? 'true' : 'false'}`,
      { method: 'POST', body: '{}' },
    ),

  list: (params?: { beneficiaryId?: number | undefined; from?: string | undefined; to?: string | undefined }) => {
    const qs = new URLSearchParams()
    if (params?.beneficiaryId !== undefined) qs.set('beneficiaryId', String(params.beneficiaryId))
    if (params?.from) qs.set('from', params.from)
    if (params?.to) qs.set('to', params.to)
    const q = qs.toString()
    return api<ActivityPayoutList>(`${PAYOUTS}${q ? `?${q}` : ''}`)
  },
}
